import { ChangeEvent, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Badge,
  Button,
  Dialog,
  DialogTitle,
  DialogFooter,
  DialogContent,
  DialogBody,
  DialogHeader,
  DialogTrigger,
  Avatar,
  AvatarImage,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ScrollArea,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shadcn/ui';
import { useToast } from '@shadcn/hooks/use-toast';
import { InputWrapper } from '@shadcn/custom/InputWrapper';
import {
  MailIcon,
  UserPlusIcon,
  Building2Icon,
  StarIcon,
  UsersIcon,
  BoltIcon,
  CrownIcon,
  Trash2Icon,
  CreditCardIcon,
  CheckIcon,
  XIcon,
  BuildingIcon,
} from 'lucide-react';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import userServices from 'app/services/user-services';
import conedService from 'app/services/coned';
import organizationServices from 'app/services/organization';
import { CLIENT_ID, generateRandomState, getConEdAuthUrl } from 'app/utils/constants/conEdison';
import useFeatureAccess from 'app/services/feature-access';

const defaultAvatarUrl =
  'https://static.vecteezy.com/system/resources/thumbnails/001/840/618/small/picture-profile-icon-male-icon-human-or-people-sign-and-symbol-free-vector.jpg';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  avatar: string;
  isCurrentUser: boolean;
}

export interface ConEdisonAccount {
  authorizationURI: string;
  customerResourceURI: string;
  resourceURI: string;
  subscriptionId: string;
  scope: string;
  tokenExpiresAt: string;
  status: 'active' | 'inactive';
}

interface ConEdCustomerAccount {
  id: number;
  customerAccountId: number;
  streetAddress: string;
  city: string;
  state: string;
  postalCode: number;
}

const STRIPE_LINKS = {
  starter: 'https://buy.stripe.com/6oE2begxfeMl13i7sz',
  professional: 'https://buy.stripe.com/fZeaHKcgZdIh6nC4go',
  business: 'https://buy.stripe.com/eVadTWdl347H7rGeV3',
};

export const Profile = () => {
  const location = useLocation();
  const { toast } = useToast();
  const { authUser } = useJumboAuth();
  // @ts-ignore
  const user = authUser?.attributes;
  const organizationName = authUser?.relationships?.organization?.data?.name;
  const organizationId = authUser?.relationships?.organization?.data?.id;
  const organizationAccountType = authUser?.relationships?.organization?.data?.accountType;
  const organizationAccountStatus = authUser?.relationships?.organization?.data?.accountStatus;
  const avatar = user?.avatar;

  const [energyStarAccount, setEnergyStarAccount] = useState<string | null>(null);
  const [conEdisonAccounts, setConEdAccounts] = useState<ConEdisonAccount[]>([]);
  const [conEdCustomerAccounts, setConEdCustomerAccounts] = useState<ConEdCustomerAccount[]>([]);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(false);
  const { currentPlan, accountStatus } = useFeatureAccess();

  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!organizationId) return;

      setIsLoadingTeamMembers(true);
      try {
        const data = await userServices.getTeamMembers(organizationId);
        const formattedMembers = data.map((member) => ({
          id: member.id,
          name: member.attributes?.firstName || 'Unknown User',
          email: member.attributes?.email,
          avatar: member.attributes?.avatar || defaultAvatarUrl,
          isCurrentUser: member.id === user?.id,
        }));
        setTeamMembers(formattedMembers);
      } catch (error) {
        console.error('Error fetching team members:', error);
        toast({
          description: 'Failed to load team members. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsLoadingTeamMembers(false);
      }
    };

    fetchTeamMembers();
  }, [organizationId, user?.id]);

  // Function to initiate ConEd connection
  const initiateConEdConnection = () => {
    // Validate configuration
    if (!CLIENT_ID) {
      toast({
        description: 'ConEd integration not properly configured. Please contact support.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Generate random state for security
      const state = generateRandomState();

      // Store state in session for verification later
      sessionStorage.setItem('coned_state', state);

      // Redirect user to ConEd
      window.location.href = getConEdAuthUrl(state);
    } catch (error) {
      console.error('Error initiating ConEd connection:', error);
      toast({
        description: 'Failed to initiate ConEd connection. Please try again.',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    if (authUser?.relationships?.organization?.data?.energyStarAccountId) {
      setEnergyStarAccount(authUser.relationships.organization.data.energyStarAccountId);
    }

    if (authUser?.relationships?.organization?.data?.conEdisonAccounts) {
      setConEdAccounts(authUser.relationships.organization.data.conEdisonAccounts);
    }
  }, [authUser]);

  useEffect(() => {
    const fetchConEdAccounts = async () => {
      try {
        const response = await conedService.getConEdAccounts();
        if (response?.customerAccounts) {
          setConEdCustomerAccounts(response.customerAccounts);
        }
      } catch (error: any) {
        console.error('Error fetching ConEd accounts:', error);

        toast({
          description: error?.errors?.[0]?.detail || 'Failed to fetch ConEd accounts.',
          variant: 'destructive',
        });
      }
    };

    // Only fetch accounts if there are active accounts
    const hasActiveAccounts = conEdisonAccounts?.some((account) => account.status === 'active');
    if (conEdisonAccounts?.length > 0 && hasActiveAccounts) {
      fetchConEdAccounts();
    }
  }, [conEdisonAccounts]);

  // Handle ConEd callback messages
  useEffect(() => {
    const state = location.state as { message?: string; error?: string } | null;
    if (state?.message) {
      toast({
        description: state.message,
        variant: 'positive',
      });
    } else if (state?.error) {
      toast({
        description: state.error,
        variant: 'destructive',
      });
    }
  }, [location, toast]);

  const handleClickOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setEmail('');
    setEmailError(false);
    setInviteSent(false);
    setInviteError('');
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailError(false);
  };

  const handleSendRequest = async () => {
    if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError(true);
    } else {
      try {
        await userServices.invite({ email });
        setInviteSent(true);
        toast({
          description: 'Invitation sent successfully!',
          variant: 'positive',
        });
        handleClose();
      } catch (error) {
        setInviteError('Failed to send invite. Please try again.');
      }
    }
  };

  const handleDeleteConEdAccount = async (accountToDelete: ConEdisonAccount) => {
    try {
      if (!organizationId) return;

      // Filter out the account to delete from conEdisonAccounts
      const updatedAccounts = conEdisonAccounts.filter(
        (account) => account.subscriptionId !== accountToDelete.subscriptionId
      );

      // Update the organization with the new accounts list
      await organizationServices.update({
        id: organizationId,
        data: {
          conEdison: updatedAccounts,
        },
      });

      // Update local state for conEdisonAccounts
      setConEdAccounts(updatedAccounts);

      // Update local state for conEdCustomerAccounts by removing the corresponding customer account
      const updatedCustomerAccounts = conEdCustomerAccounts.filter(
        (ca) => ca.customerAccountId.toString() !== accountToDelete.subscriptionId
      );
      setConEdCustomerAccounts(updatedCustomerAccounts);

      // If we're deleting the last account, clear customer accounts
      if (updatedAccounts.length === 0) {
        setConEdCustomerAccounts([]);
      }

      toast({
        description: 'ConEdison account disconnected successfully.',
        variant: 'positive',
      });
    } catch (error) {
      console.error('Error deleting ConEd account:', error);
      toast({
        description: 'Failed to disconnect ConEdison account. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const getPlanFeatures = () => {
    return {
      uploadUtilityBills: {
        residential: true,
        starter: true,
        professional: true,
        business: true,
        label: 'Upload Utility Bills',
      },
      energyStarIntegration: {
        residential: false,
        starter: true,
        professional: true,
        business: true,
        label: 'ENERGY STAR Integration',
      },
      portfolioAnalysis: {
        residential: false,
        starter: true,
        professional: true,
        business: true,
        label: 'Portfolio-Level Energy and Emissions Analysis',
      },
      complianceTracking: {
        residential: false,
        starter: false,
        professional: true,
        business: true,
        label: 'Compliance Tracking (Local Law Coverage)',
      },
      exportEmissionsReports: {
        residential: false,
        starter: false,
        professional: false,
        business: true,
        label: 'Export Emissions Reports',
      },
      realTimeMonitoring: {
        residential: false,
        starter: false,
        professional: false,
        business: true,
        label: 'Real-Time Energy Monitoring with ConEdison',
      },
    };
  };

  const getPlanDetails = () => {
    return {
      residential: {
        name: 'Residential',
        price: 'Free',
        description: 'For individual homeowners',
        buildings: 1,
        isPopular: false,
      },
      starter: {
        name: 'Starter',
        price: '$99/mo',
        description: 'Up to 15 buildings',
        buildings: 15,
        isPopular: false,
      },
      professional: {
        name: 'Professional',
        price: '$499/mo',
        description: 'Up to 75 buildings',
        buildings: 75,
        isPopular: true,
      },
      business: {
        name: 'Business',
        price: '$999/mo',
        description: 'Up to 200 buildings',
        buildings: 200,
        isPopular: false,
      },
    };
  };

  return (
    <ScrollArea className="grow">
      <div className="container mx-auto max-w-6xl space-y-8 p-8">
        {/* Profile Section */}
        <Card className="overflow-hidden border-border/20 bg-background/5 shadow-lg backdrop-blur-sm">
          <div className="relative h-32 bg-gradient-to-r from-primary/30 to-primary/20">
            <div className="absolute -bottom-12 left-8">
              <Avatar className="h-24 w-24 border-4 border-border/20 bg-background/5 shadow-xl backdrop-blur-sm">
                <AvatarImage src={avatar || defaultAvatarUrl} alt={user?.firstName || 'User'} />
              </Avatar>
            </div>
          </div>
          <CardContent className="mt-16">
            <div className="flex flex-col gap-1">
              <h2 className="text-2xl font-bold">{user?.firstName || 'User Name'}</h2>
              <p className="flex items-center gap-2 text-muted-foreground/80">
                <MailIcon className="h-4 w-4" />
                {user?.email}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Organization Section */}
        <Card className="border-border/20 bg-background/5 shadow-lg backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2Icon className="h-5 w-5" />
              <div className="flex items-center gap-2">
                <p className="text-lg font-semibold">{organizationName}</p>
                {organizationAccountType && (
                  <Badge className="pointer-events-none border-0 bg-primary/20 text-primary">
                    {organizationAccountType}
                  </Badge>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Integrations Section */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground/80">Integrations</h3>
              <div className="space-y-4">
                {/* Energy Star Integration */}
                {organizationAccountType !== 'residential' && (
                  <div className="rounded-lg border border-border/20 bg-background/5 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <StarIcon className="h-5 w-5 text-yellow-400" />
                        <h4 className="font-medium">Energy Star Portfolio Manager</h4>
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          energyStarAccount ? 'bg-emerald-500/20 text-emerald-400' : 'bg-muted text-muted-foreground/80'
                        }
                      >
                        {energyStarAccount ? 'Connected' : 'Not Connected'}
                      </Badge>
                    </div>
                    {energyStarAccount && (
                      <p className="text-sm text-muted-foreground/80">Account ID: {energyStarAccount}</p>
                    )}
                  </div>
                )}

                {/* ConEd Integration */}
                <div className="rounded-lg border border-border/20 bg-background/5 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BoltIcon className="h-5 w-5 text-yellow-400" />
                      <h4 className="font-medium">ConEdison Real-Time Data</h4>
                    </div>
                    <div className="relative">
                      <Button
                        onClick={initiateConEdConnection}
                        variant="outline"
                        size="sm"
                        className="mt-4 border-emerald-500/30 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                      >
                        {conEdisonAccounts?.length ? 'Connect Another Account' : 'Connect'}
                      </Button>
                    </div>
                  </div>
                  {conEdisonAccounts?.length > 0 ? (
                    <div className="mt-4 space-y-3">
                      <p className="text-sm text-muted-foreground/80">Connected Accounts:</p>
                      {conEdisonAccounts.map((account) => {
                        const customerAccount = conEdCustomerAccounts.find(
                          (ca) => ca.customerAccountId.toString() === account.subscriptionId
                        );

                        return (
                          <div
                            key={account.subscriptionId}
                            className="rounded-md border border-border/20 bg-background/5 p-3"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">Account ID: {account.subscriptionId}</p>
                                  <Badge
                                    variant="outline"
                                    className={
                                      account.status === 'active'
                                        ? 'bg-emerald-500/20 text-emerald-400'
                                        : 'bg-yellow-500/20 text-yellow-400'
                                    }
                                  >
                                    {account.status === 'active' ? 'Active' : 'Inactive'}
                                  </Badge>
                                </div>
                                {customerAccount ? (
                                  <p className="mt-1 text-sm text-muted-foreground/80">
                                    {customerAccount.streetAddress}, {customerAccount.city}, {customerAccount.state}{' '}
                                    {customerAccount.postalCode}
                                  </p>
                                ) : account.status === 'inactive' ? (
                                  <p className="mt-1 text-sm text-yellow-400">
                                    Account needs to be reconnected to view details
                                  </p>
                                ) : null}
                              </div>
                              <Button
                                onClick={() => handleDeleteConEdAccount(account)}
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:bg-red-400/10 hover:text-red-400"
                              >
                                <Trash2Icon className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground/80">
                      Connect to ConEdison to access real-time utility data.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team Members Section */}
        <Card className="border-border/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <UsersIcon className="h-5 w-5" />
                Members
              </CardTitle>
              <Dialog open={open} onOpenChange={(newOpen) => (newOpen ? handleClickOpen() : handleClose())}>
                <DialogTrigger asChild>
                  <Button
                    onClick={handleClickOpen}
                    variant="outline"
                    size="sm"
                    className="border-border/20 bg-background/5 hover:bg-background/10"
                  >
                    <UserPlusIcon className="mr-2 h-4 w-4" />
                    Invite Member
                  </Button>
                </DialogTrigger>
                <DialogContent className="border-border/20 bg-background/95 shadow-xl backdrop-blur-sm">
                  <DialogHeader>
                    <DialogTitle>Invite to {organizationName}</DialogTitle>
                  </DialogHeader>
                  <DialogBody>
                    <p className="text-sm text-muted-foreground/80">
                      Enter the email address of the user you'd like to invite to join the organization.
                    </p>
                    <InputWrapper
                      autoFocus
                      id="email"
                      label="Email Address"
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      error={emailError}
                      helperText={emailError ? 'Please enter a valid email' : undefined}
                      className="bg-background/50"
                    />
                    {inviteSent ? <p className="text-sm text-emerald-400">Invitation sent successfully!</p> : null}
                    {inviteError ? <p className="text-sm text-red-400">{inviteError}</p> : null}
                  </DialogBody>
                  <DialogFooter>
                    <Button
                      onClick={handleClose}
                      variant="secondary"
                      className="bg-background/5 hover:bg-background/10"
                    >
                      {inviteSent ? 'Close' : 'Cancel'}
                    </Button>
                    <Button
                      onClick={handleSendRequest}
                      disabled={!email || inviteSent}
                      className="bg-primary/90 text-primary-foreground hover:bg-primary"
                    >
                      Send Invitation
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-border/20">
                  <TableHead className="text-muted-foreground/80">Member</TableHead>
                  <TableHead className="text-muted-foreground/80">Role</TableHead>
                  <TableHead className="text-muted-foreground/80">Email</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingTeamMembers ? (
                  <TableRow className="border-border/20">
                    <TableCell colSpan={3} className="py-8 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <UsersIcon className="h-4 w-4 animate-pulse text-muted-foreground" />
                        <span className="text-muted-foreground/80">Loading team members...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : teamMembers.length === 0 ? (
                  <TableRow className="border-border/20">
                    <TableCell colSpan={3} className="py-8 text-center">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <UsersIcon className="h-8 w-8 text-muted-foreground" />
                        <span className="text-muted-foreground/80">No team members found</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  teamMembers.map((member) => (
                    <TableRow key={member.id} className="border-border/20">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8 border border-border/20">
                            <AvatarImage src={member.avatar} />
                          </Avatar>
                          <div>
                            <p className="font-medium">{member.name}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="pointer-events-none flex w-fit items-center gap-1 border-0 bg-primary/20 text-primary">
                          <CrownIcon className="h-3 w-3" />
                          Member
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground/80">{member.email}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Billing Section */}
        <Card className="border-border/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCardIcon className="h-5 w-5" />
              Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <h3 className="text-lg font-medium">
                Current Plan:{' '}
                <Badge className="ml-2 border-0 bg-primary/20 text-primary">{currentPlan || 'Residential'}</Badge>
              </h3>
              {currentPlan === 'residential' && (
                <p className="mt-2 text-sm text-muted-foreground/80">
                  Upgrade your plan to access more features and manage more buildings.
                </p>
              )}

              {accountStatus === 'inactive' && (
                <p className="mt-2 text-sm text-muted-foreground/80">
                  Your account is currently inactive. Renew your subscription to continue using the platform.
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {Object.entries(getPlanDetails()).map(([planType, details]) => (
                <div
                  key={planType}
                  className={`relative flex flex-col rounded-lg border ${
                    currentPlan === planType ? 'border-primary bg-primary/5' : 'border-border/20 bg-background/5'
                  } p-5 shadow-sm`}
                >
                  {details.isPopular && (
                    <div className="absolute -top-3 right-4 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-primary-foreground">
                      Popular
                    </div>
                  )}

                  <div className="mb-4">
                    <h3 className="text-lg font-semibold">{details.name}</h3>
                    <div className="mt-1 flex items-baseline">
                      <span className="text-2xl font-bold">{details.price}</span>
                    </div>
                    <p className="mt-1 flex items-center text-sm text-muted-foreground/80">
                      <BuildingIcon className="mr-1 h-4 w-4" />
                      {details.description}
                    </p>
                  </div>

                  {currentPlan === planType ? (
                    <Button
                      variant="outline"
                      disabled={organizationAccountStatus !== 'inactive'}
                      onClick={() =>
                        organizationAccountStatus === 'inactive' && window.open(STRIPE_LINKS[planType], '_blank')
                      }
                      className={`mt-auto w-full ${
                        organizationAccountStatus === 'inactive'
                          ? 'border-primary/50 bg-primary/10 hover:bg-primary/20'
                          : 'border-primary/50 bg-primary/10'
                      }`}
                    >
                      {organizationAccountStatus === 'inactive' ? 'Renew Subscription' : 'Current Plan'}
                    </Button>
                  ) : (
                    <Button
                      variant={planType === 'residential' ? 'outline' : 'default'}
                      className={`mt-auto w-full ${
                        planType === 'residential'
                          ? 'border-border/20 bg-background/5'
                          : 'bg-primary/90 text-primary-foreground hover:bg-primary'
                      }`}
                      onClick={() => planType !== 'residential' && window.open(STRIPE_LINKS[planType], '_blank')}
                      disabled={planType === 'residential'}
                    >
                      {planType === 'residential' ? 'Free Plan' : 'Upgrade'}
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/20">
                    <TableHead className="text-muted-foreground/80">Feature</TableHead>
                    <TableHead className="text-center text-muted-foreground/80">Residential</TableHead>
                    <TableHead className="text-center text-muted-foreground/80">Starter</TableHead>
                    <TableHead className="text-center text-muted-foreground/80">Professional</TableHead>
                    <TableHead className="text-center text-muted-foreground/80">Business</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(getPlanFeatures()).map(([featureKey, feature]) => (
                    <TableRow key={featureKey} className="border-border/20">
                      <TableCell>{feature.label}</TableCell>
                      <TableCell className="text-center">
                        {feature.residential ? (
                          <CheckIcon className="mx-auto h-5 w-5 text-emerald-500" />
                        ) : (
                          <XIcon className="mx-auto h-5 w-5 text-muted-foreground/50" />
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {feature.starter ? (
                          <CheckIcon className="mx-auto h-5 w-5 text-emerald-500" />
                        ) : (
                          <XIcon className="mx-auto h-5 w-5 text-muted-foreground/50" />
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {feature.professional ? (
                          <CheckIcon className="mx-auto h-5 w-5 text-emerald-500" />
                        ) : (
                          <XIcon className="mx-auto h-5 w-5 text-muted-foreground/50" />
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {feature.business ? (
                          <CheckIcon className="mx-auto h-5 w-5 text-emerald-500" />
                        ) : (
                          <XIcon className="mx-auto h-5 w-5 text-muted-foreground/50" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </ScrollArea>
  );
};
